import { matchPath, useLocation } from "react-router";

import type { BreadcrumbAncestor, BreadcrumbConfigEntry, BreadcrumbParentResource } from "../config/breadcrumbConfig";
import { breadcrumbConfig } from "../config/breadcrumbConfig";

export type BreadcrumbItem = Readonly<{
    labelKey: string;
    /** Omit for non-clickable crumbs (menu category labels, or resources with no page to link to). */
    to?: string;
    /** Redux tab key to activate (via setDefaultActiveKey) before navigating to `to`. */
    tabKey?: string;
}>;

/** Most specific (longest) route pattern first, so overlapping patterns don't shadow a more precise match. */
const configPathsBySpecificity = Object.keys(breadcrumbConfig).sort((a, b) => b.length - a.length);

/** Replaces every `:token` in a path template using `params`, leaving unmatched tokens untouched. */
const fillPathParams = (path: string, params: Readonly<Record<string, string | undefined>>): string =>
    path.replace(/:([A-Za-z0-9_]+)/g, (placeholder, key: string) => params[key] ?? placeholder);

const isParentResource = (ancestor: BreadcrumbAncestor): ancestor is BreadcrumbParentResource => ancestor.kind === "parentResource";

/**
 * Builds the link to a parent record's edit page. A parent route can itself be nested (e.g.
 * Card Definition's edit route also carries `:cardImplementationId`) — those extra tokens are
 * filled directly from the current match's params, since they share the same param name at every
 * level of the chain. Only the parent's OWN id (always named `:id` on its own route) needs the
 * indirection through `paramName`, since the current (child) route names that value differently.
 */
const buildParentLink = (parent: BreadcrumbParentResource, params: Readonly<Record<string, string | undefined>>): string | undefined => {
    const parentId = params[parent.paramName];
    return parentId ? fillPathParams(parent.route.path, { ...params, id: parentId }) : undefined;
};

const buildAncestorItem = (ancestor: BreadcrumbAncestor, params: Readonly<Record<string, string | undefined>>): BreadcrumbItem => {
    if (!isParentResource(ancestor)) {
        return { labelKey: ancestor.labelKey };
    }

    return { labelKey: ancestor.labelKey, to: buildParentLink(ancestor, params) };
};

const buildSelfItem = (config: BreadcrumbConfigEntry, params: Readonly<Record<string, string | undefined>>): BreadcrumbItem => {
    if (config.to) {
        return { labelKey: config.labelKey, to: config.to };
    }

    if (config.selfTabKey) {
        const immediateParent = (config.ancestors ?? []).findLast(isParentResource);
        const to = immediateParent && buildParentLink(immediateParent, params);

        if (to) {
            return { labelKey: config.labelKey, to, tabKey: config.selfTabKey };
        }
    }

    return { labelKey: config.labelKey };
};

/**
 * Builds the breadcrumb trail for a given pathname, purely from the static route hierarchy
 * (see {@link breadcrumbConfig}). Returns undefined for pathnames that aren't a known create/edit page.
 */
export const getBreadcrumbItems = (pathname: string): ReadonlyArray<BreadcrumbItem> | undefined => {
    for (const path of configPathsBySpecificity) {
        const match = matchPath({ path, end: true }, pathname);
        if (!match) {
            continue;
        }

        const config = breadcrumbConfig[path];
        const params = match.params as Readonly<Record<string, string | undefined>>;
        const ancestorItems = (config.ancestors ?? []).map((ancestor) => buildAncestorItem(ancestor, params));
        const selfItem = buildSelfItem(config, params);
        const detailItem: BreadcrumbItem = { labelKey: "breadcrumb.detail" };

        return [...ancestorItems, selfItem, detailItem];
    }

    return undefined;
};

export const useBreadcrumbs = (): ReadonlyArray<BreadcrumbItem> | undefined => {
    const { pathname } = useLocation();
    return getBreadcrumbItems(pathname);
};
